import { darken } from "polished";
import React from "react";
import { Observer } from "mobx-react";
import { Paper, withStyles } from "@material-ui/core";
import SearchPageLayout from "@search/components/SearchPageLayout";
import SearchOptions from "@search/SearchOptions";

import { TimereportKitSupportSearchStore } from "./TimereportKitSupportSearchStore";
import { getColumns } from "./TimereportKitSupportSearchColumns";
import { BookingSearchForm } from "../../bookings/BookingSearch/BookingSearchForm";
import { BookingFilterGroup } from "../../bookings/BookingSearch/utils";
import { BookingSearchActionBar } from "../../bookings/BookingSearch/BookingSearchActionBar";
import { DefaultInfinityTableRow, InfiniteScroll } from "@search/InfiniteScroll";
import { AutoSizer } from "react-virtualized";
import ColumnSizer from "@search/TableSearch/ColumnSizer";
import TableSearchHeader from "@search/TableSearch/TableSearchHeader";
import { Classes, ThemeType } from "@theme";
import { BookingProfileLocation } from "@navigation";
import { rowRendererLink } from "@common/renderers";
import { BookingFinanceSearchItemFragment } from "@graphql";

type Props = {
  onOpen: (id: string, tab?: string) => void;
  onEdit: (id?: string) => void;
  classes: Classes<typeof styles>;
};

export class TimereportKitSupportSearch extends React.Component<Props> {
  store = new TimereportKitSupportSearchStore();
  interval: any;

  componentWillUnmount() {
    this.store.unsubscribe();

    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  componentDidMount() {
    this.interval = setInterval(() => {
      this.store.refetch();
    }, 5000);
  }

  render() {
    return (
      <SearchPageLayout
        delayed
        wrapContent={(content) => <Paper>{content}</Paper>}
        actionBar={
          <Observer>
            {() => (
              <>
                <BookingSearchActionBar
                  onDirectBooking={() => this.props.onEdit()}
                  total={this.store.total}
                  withSyncTimeReport
                  bookingIds={
                    this.store?.searchManager?.items?.map((i) => i.BookingRef?.NodeID) ??
                    []
                  }
                />

                <SearchOptions />
              </>
            )}
          </Observer>
        }
        content={(scrollContainer) => (
          <Observer>
            {() => (
              <InfiniteScroll
                scrollContainer={scrollContainer}
                search={this.store.filterManager?.filter.text}
                items={this.store.searchManager.items}
                loadMore={this.store.searchManager.loadMoreRows}
                hasMore={this.store.hasMore}
                dataLength={this.store.searchManager.rowCount}
                renderHead={<TableHeadRender />}
                loading={this.store.searchManager.isLoading}
                renderRow={rowRendererLink(
                  this.rowRenderer,
                  (item: BookingFinanceSearchItemFragment) => {
                    const bookingId = item?.BookingRef?.NodeID;

                    if (!bookingId) {
                      return;
                    }

                    return {
                      to: new BookingProfileLocation(bookingId).toURI(),
                      onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        this.props.onOpen(bookingId);
                      }
                    };
                  }
                )}
              />
            )}
          </Observer>
        )}
        filter={this.renderFilter()}
      />
    );
  }

  private renderFilter = () => {
    return (
      <div>
        <BookingSearchForm
          manager={this.store.filterManager}
          group={BookingFilterGroup.KitSupport}
        />
      </div>
    );
  };

  private rowRenderer = (item, index) => {
    const { classes } = this.props;
    const columns = getColumns();
    return (
      <DefaultInfinityTableRow
        item={item}
        key={item._id}
        rowIndex={index}
        columns={columns}
        rowClassNames={
          item.isHighPrior ? `${classes.row} ${classes.rowInPriority}` : classes.row
        }
      />
    );
  };
}

const TableHeadRender = () => {
  const columns = getColumns();
  return (
    <AutoSizer disableHeight>
      {({ width }) => (
        <div style={{ background: "white", position: "relative", width }}>
          <ColumnSizer columns={columns} width={width}>
            {({ ref }) => (
              <TableSearchHeader
                columns={columns}
                columnSizer={ref}
                style={{ backgroundColor: "#fff" }}
              />
            )}
          </ColumnSizer>
        </div>
      )}
    </AutoSizer>
  );
};

const bg = (color) => ({
  backgroundColor: color,
  "&.odd": { backgroundColor: darken(0.02, color) },
  "&:hover": { backgroundColor: darken(0.02 * 5, color) },
  "&.odd:hover": { backgroundColor: darken(0.02 * 5, color) }
});

const styles = (theme: ThemeType) => ({
  row: {
    "& div": {
      fontSize: 14,
      padding: 0
    },
    "& div > span, & div > p": {
      fontSize: 14
    }
  },
  rowInPriority: bg("#F9F0DA")
});

export default withStyles(styles as any)(TimereportKitSupportSearch);
